import { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LoginPage } from "./pages/login";
import { MessagesPage } from "./pages/messages";
import { NewMessagePage } from "./pages/new-message";
import { CompaniesPage } from "./pages/companies";
import { ProfilePage } from "./pages/profile";
import { ForgotPasswordPage } from "./pages/forgot-password";
import { MessageDetailsPage } from "./pages/message-details";
import { SplashScreen } from "./components/SplashScreen";
import { createSolicitation, getTemplate } from "./services/api";
import { updateTheme, loadLogoBase64 } from "./utils/themeManager";
import { toast } from "react-toastify";
import useStore from "./store/useStore";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const syncOfflineMessages = async () => {
      try {
        const cachedRaw = localStorage.getItem("offline-messages");
        if (!cachedRaw) return; // Nenhuma mensagem offline
  
        const cached = JSON.parse(cachedRaw);
        if (!Array.isArray(cached) || cached.length === 0) return;
  
        for (const msg of cached) {
          try {
            await createSolicitation(msg);
            console.log("Mensagem offline enviada com sucesso:", msg);
          } catch (error) {
            console.error("Erro ao tentar reenviar mensagem offline:", error);
            // ❗ Sai do loop imediatamente. Não remove as mensagens do localStorage.
            return;
          }
        }
  
        // ✅ Só limpa o localStorage se todas foram enviadas com sucesso
        localStorage.removeItem("offline-messages");
        toast.success("Mensagens offline enviadas com sucesso", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
        });
  
      } catch (error) {
        console.error("Erro ao processar mensagens offline:", error);
        // Se ocorrer erro geral (JSON inválido, etc), não faz nada e mantém o cache
      }
};

function AppContent() {
  const [showSplash, setShowSplash] = useState(true);
  const location = useLocation();
  const { isOnline, setIsOnline } = useStore();

  useEffect(() => {
    const restored = useStore.getState().authUser;
    if (!restored) return;

    // Sessao restaurada nao passa pelo login, entao rebusca o tema do cliente
    // aqui — senao o app cai no tema padrao (verde + logo "financeiro na mao").
    (async () => {
      const newTheme = await getTemplate(Number(restored?.bpo?.id));
      if (newTheme) {
        updateTheme(newTheme);
        loadLogoBase64();
      }
      (window as any).ReactNativeWebView?.postMessage(
        JSON.stringify({ type: "SET_AUTH", payload: { theme: newTheme, user: restored } })
      );
    })();
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  
  // Efeito para sincronizar mensagens offline
  useEffect(() => {
    // Chamar imediatamente ao abrir o app, caso esteja online
    if (isOnline)
      syncOfflineMessages();

  }, [isOnline]);

  // Efeito para controlar a tela de splash
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  if (showSplash) {
    return <SplashScreen />;
  }


  return (
  // @ts-ignore
    <Switch>
      {/* @ts-ignore */}
      <Route exact path="/" component={CompaniesPage} />
      {/* @ts-ignore */}
      <Route exact path="/login" component={LoginPage} />
      {/* @ts-ignore */}
      <Route exact path="/companies" component={CompaniesPage} />
      {/* @ts-ignore */}
      <Route exact path="/messages" component={MessagesPage} />
      {/* @ts-ignore */}
      <Route exact path="/message-details" component={MessageDetailsPage} />
      {/* @ts-ignore */}
      <Route exact path="/new-message" component={NewMessagePage} />
      {/* @ts-ignore */}
      <Route exact path="/profile" component={ProfilePage} />
      {/* @ts-ignore */}
      <Route exact path="/forgot-password" component={ForgotPasswordPage} />
    </Switch>
  );
}

function App() {
  return (
    // @ts-ignore
    <Router>
      <QueryClientProvider client={queryClient}>
        <AppContent />
      </QueryClientProvider>
    </Router>
  );
}

export default App;
